import { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Info, CheckCircle, AlertTriangle, AlertOctagon, X, ExternalLink } from 'lucide-react';
import axios from 'axios';
import { BACKEND_URL } from '../config/api';

// Reuse the same colour tokens as DashboardBanner for consistency.
const TYPE_STYLES = {
  info: { bg: 'bg-sky-50 border-sky-200', icon: Info, iconColor: 'text-sky-600', textColor: 'text-sky-900' },
  success: { bg: 'bg-emerald-50 border-emerald-200', icon: CheckCircle, iconColor: 'text-emerald-600', textColor: 'text-emerald-900' },
  warning: { bg: 'bg-amber-50 border-amber-200', icon: AlertTriangle, iconColor: 'text-amber-600', textColor: 'text-amber-900' },
  urgent: { bg: 'bg-red-50 border-red-200', icon: AlertOctagon, iconColor: 'text-red-600', textColor: 'text-red-900' },
};

const ROTATE_MS = 10000;
const DISMISS_STORAGE_KEY = 'promo_banner_dismissed_version';

// Routes where the promo banner must NOT render.
// Admin dashboard pages and auth flows are excluded per spec.
function isExcluded(pathname) {
  if (!pathname) return false;
  return (
    pathname.startsWith('/login')
    || pathname.startsWith('/register')
    || pathname.startsWith('/auth/')
    || pathname.startsWith('/admin')
    || pathname.startsWith('/verify-email')
    || pathname.startsWith('/forgot-password')
    || pathname.startsWith('/reset-password')
    || pathname.startsWith('/change-password')
  );
}

/**
 * PromoBanner — rotating promotional strip rendered under the top nav.
 * - Fetches /api/dashboard-banner/promo/messages on mount (no polling).
 * - Rotates active messages every 10s (ROTATE_MS).
 * - Hides itself when there are no active messages (empty state).
 * - Session-level dismissal via sessionStorage (per spec).
 */
export default function PromoBanner() {
  const location = useLocation();
  const [messages, setMessages] = useState([]);
  const [version, setVersion] = useState(null);
  const [idx, setIdx] = useState(0);
  const [dismissed, setDismissed] = useState(false);

  // Re-fetch whenever the pathname changes so admin edits propagate on
  // next page view (no manual redeploy needed, per spec).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/dashboard-banner/promo/messages`);
        if (cancelled) return;
        setMessages(Array.isArray(data?.messages) ? data.messages : []);
        setVersion(data?.version || null);
        setIdx(0);
      } catch {
        if (!cancelled) {
          setMessages([]);
          setVersion(null);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [location.pathname]);

  // Session dismissal — persists for the tab/session only, resets if the
  // admin edits any message (new `version`).
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem(DISMISS_STORAGE_KEY);
      setDismissed(Boolean(saved) && saved === version);
    } catch {
      setDismissed(false);
    }
  }, [version]);

  // Rotation timer — only runs when there's more than one message.
  useEffect(() => {
    if (messages.length <= 1) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % messages.length), ROTATE_MS);
    return () => clearInterval(t);
  }, [messages.length]);

  const excluded = useMemo(() => isExcluded(location.pathname), [location.pathname]);
  if (excluded) return null;
  if (dismissed) return null;
  if (!messages.length) return null;

  const current = messages[Math.min(idx, messages.length - 1)];
  const style = TYPE_STYLES[current.banner_type] || TYPE_STYLES.info;
  const Icon = style.icon;

  const handleDismiss = () => {
    try { sessionStorage.setItem(DISMISS_STORAGE_KEY, version || 'v0'); } catch { /* ignore */ }
    setDismissed(true);
  };

  return (
    <div
      className={`w-full border-b ${style.bg}`}
      data-testid="promo-banner"
      role="region"
      aria-label="Promotional banner"
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-2 flex items-center gap-2 sm:gap-3">
        <Icon className={`h-4 w-4 shrink-0 ${style.iconColor}`} />
        <div className="flex-1 min-w-0 text-sm" data-testid="promo-banner-text">
          <span className={`${style.textColor} truncate`}>{current.text}</span>
          {current.cta_label && current.cta_url && (
            <a
              href={current.cta_url}
              target={current.cta_url.startsWith('http') ? '_blank' : '_self'}
              rel="noopener noreferrer"
              className={`ml-2 inline-flex items-center gap-1 font-medium underline underline-offset-2 ${style.textColor}`}
              data-testid="promo-banner-cta"
            >
              {current.cta_label}
              {current.cta_url.startsWith('http') && <ExternalLink className="h-3 w-3" />}
            </a>
          )}
        </div>
        {messages.length > 1 && (
          <div className="hidden sm:flex items-center gap-1" aria-label="Rotation progress">
            {messages.map((m, i) => (
              <span
                key={m.id}
                className={`h-1.5 w-1.5 rounded-full ${i === idx ? style.iconColor.replace('text-', 'bg-') : 'bg-black/10'}`}
              />
            ))}
          </div>
        )}
        <button
          onClick={handleDismiss}
          className="shrink-0 p-1 rounded-md hover:bg-black/5 transition-colors"
          aria-label="Dismiss promotional banner"
          data-testid="promo-banner-dismiss"
        >
          <X className="h-4 w-4 text-slate-500" />
        </button>
      </div>
    </div>
  );
}
